import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, FilePdf, Image as ImageIcon, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../lib/api";

const CATEGORY_LABEL = {
  letter: "Clinic letter",
  result: "Test result",
  imaging: "Imaging report",
  patient_upload: "You uploaded",
};

const CATEGORY_BADGE = {
  letter: "bg-cream-100 text-bronze-dark border-bronze-light/60",
  result: "bg-cream-100 text-bronze-dark border-bronze-light/60",
  imaging: "bg-cream-100 text-bronze-dark border-bronze-light/60",
  patient_upload: "bg-stone-100 text-stone-600 border-stone-300",
};

function fileIcon(ct) {
  if (!ct) return <FileText size={18} />;
  if (ct.includes("pdf")) return <FilePdf size={18} weight="duotone" />;
  if (ct.startsWith("image/")) return <ImageIcon size={18} weight="duotone" />;
  return <FileText size={18} />;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const OK_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const fileProblem = (file, title) => {
  if (!file) return "Please choose a file to upload.";
  if (!title.trim()) return "Please add a short title.";
  if (file.size > 25 * 1024 * 1024) return "Files must be 25 MB or smaller.";
  if (!OK_TYPES.includes(file.type)) return "Only PDF, JPG or PNG files are accepted.";
  return null;
};

const DocItem = ({ d, onDownload }) => (
  <li data-testid={`patient-doc-${d.id}`} className="py-4 flex items-start gap-4">
    <div className="text-bronze-dark mt-1">{fileIcon(d.content_type)}</div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-serif text-lg truncate">{d.title}</span>
        <span className={`text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 ${CATEGORY_BADGE[d.category] || ""}`}>
          {CATEGORY_LABEL[d.category] || d.category}
        </span>
      </div>
      <div className="text-xs text-ink-muted mt-1">
        {new Date(d.uploaded_at).toLocaleString()} · {fmtSize(d.file_size)} · {d.file_name}
      </div>
    </div>
    <button type="button" onClick={() => onDownload(d)} data-testid={`patient-doc-download-${d.id}`}
      className="inline-flex items-center gap-1.5 text-sm text-bronze-dark hover:text-bronze">
      <DownloadSimple size={16} /> View
    </button>
  </li>
);

const DocsState = ({ loading, error, docs, onDownload }) => (
  <>
    {loading && <div className="mt-6 text-sm text-ink-muted">Loading…</div>}
    {error && <div className="mt-6 text-sm text-destructive" data-testid="patient-docs-error">{error}</div>}
    {!loading && !error && docs.length === 0 && (
      <div data-testid="patient-docs-empty" className="mt-6 p-6 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">
        No documents yet. When your consultant releases a clinic letter, test result or imaging report, it will appear here and we&apos;ll email you to let you know.
      </div>
    )}
    {!loading && docs.length > 0 && (
      <ul className="mt-6 divide-y divide-stone-200/60" data-testid="patient-docs-list">
        {docs.map((d) => <DocItem key={d.id} d={d} onDownload={onDownload} />)}
      </ul>
    )}
  </>
);

const UploadForm = ({ onSubmit, title, setTitle, fileRef, uploading }) => (
  <form onSubmit={onSubmit} data-testid="patient-upload-form" className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
    <div className="eyebrow">Send something to your clinic</div>
    <h3 className="font-serif text-xl mt-2">Share home blood-pressure readings, GP letters or photos.</h3>
    <p className="mt-2 text-xs text-ink-muted">PDF, JPG or PNG. Up to 25 MB. We&apos;ll see this in your record next time you&apos;re seen.</p>
    <div className="mt-5 grid sm:grid-cols-12 gap-3 items-end">
      <label className="sm:col-span-6 block">
        <span className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">Title</span>
        <input type="text" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. BP diary May 2026" data-testid="patient-upload-title"
          className="w-full rounded-sm border border-stone-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-bronze-dark/40 focus:border-bronze-dark" />
      </label>
      <label className="sm:col-span-4 block">
        <span className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">File</span>
        <input type="file" ref={fileRef} accept="application/pdf,image/jpeg,image/png" required data-testid="patient-upload-file"
          className="block w-full text-sm text-ink file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-bronze-dark file:text-white hover:file:bg-bronze" />
      </label>
      <button type="submit" disabled={uploading} data-testid="patient-upload-submit"
        className="sm:col-span-2 btn-primary inline-flex items-center justify-center gap-1.5">
        <UploadSimple size={16} /> {uploading ? "Sending…" : "Send"}
      </button>
    </div>
  </form>
);

export default function PatientDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/documents");
      setDocs(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    try {
      const res = await fetch(`${API}/me/documents/${doc.id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not download document.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    const problem = fileProblem(file, title);
    if (problem) return toast.error(problem);

    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    setUploading(true);
    try {
      await api.post("/me/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent to the clinic.");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="patient-documents" className="space-y-8">
      <div className="p-6 bg-white border border-stone-200/60 rounded-md">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow">Your documents</div>
            <h2 className="font-serif text-2xl mt-2">Letters, results and imaging.</h2>
          </div>
          <p className="text-xs text-ink-muted max-w-sm">
            For your privacy we never email clinical information. Documents are only available here, behind your secure log-in.
          </p>
        </div>

        <DocsState loading={loading} error={error} docs={docs} onDownload={download} />
      </div>

      <p data-testid="patient-upload-disabled" className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md text-sm text-ink-muted">
        Sending documents through the portal is currently switched off. Please bring any documents to your appointment.
      </p>
    </div>
  );
}
